/**
 * WCAG 2 contrast on opaque `#rrggbb` colours, pure. The same rule as the apps'
 * (`WcagContrast.kt`, `WcagContrast.swift`), so a brand's accent is shaded the same way on
 * every screen: a colour that does not reach its minimum on a background becomes a deeper
 * (or lighter) shade of itself, and only failing that is it replaced.
 */

/** Body text (WCAG 1.4.3). */
export const TEXT = 4.5;
/** Focus indicators, large text and other large elements (WCAG 1.4.11). */
export const NON_TEXT = 3;

export type Rgb = readonly [number, number, number];

export const WHITE: Rgb = [255, 255, 255];
export const BLACK: Rgb = [0, 0, 0];

/** `#ff5a36` (the manifest's spelling, the `#` optional) to channels; null for anything else. */
export function parseHex(hex: string | null | undefined): Rgb | null {
  const value = hex?.trim().replace(/^#/, "") ?? "";
  if (!/^[0-9a-fA-F]{6}$/.test(value)) return null;
  const n = Number.parseInt(value, 16);
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
}

export const toHex = (rgb: Rgb): string => `#${rgb.map((c) => c.toString(16).padStart(2, "0")).join("")}`;

export function luminance([r, g, b]: Rgb): number {
  const channel = (value: number) => {
    const c = value / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** From 1 (the same luminance) to 21 (black on white); the order does not matter. */
export function ratio(a: Rgb, b: Rgb): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/**
 * `colour` stepped toward white (on a dark background) or black (on a light one) until it
 * reaches `minimum`, keeping its hue as far as possible; null when twenty steps do not get there.
 */
export function adjusted(colour: Rgb, background: Rgb, minimum: number): Rgb | null {
  const towards = luminance(background) < 0.5 ? WHITE : BLACK;
  let current = colour;
  for (let step = 0; step < 20; step++) {
    if (ratio(current, background) >= minimum) return current;
    current = mix(current, towards, 0.08);
  }
  return ratio(current, background) >= minimum ? current : null;
}

/** Black or white, whichever reads better on `colour`. */
export const readableOn = (colour: Rgb): Rgb => (ratio(colour, WHITE) >= ratio(colour, BLACK) ? WHITE : BLACK);

/**
 * `from` moved `amount` (0 to 1) of the way to `to`, per channel in 8-bit sRGB, truncated and clamped
 * to 0…255 (docs/brand-colour-tokens.md: `mix(a, b, t)`, the same on every platform).
 */
export function mix(from: Rgb, to: Rgb, amount: number): Rgb {
  const channel = (i: 0 | 1 | 2) => Math.min(255, Math.max(0, Math.trunc(from[i] + (to[i] - from[i]) * amount)));
  return [channel(0), channel(1), channel(2)];
}
